import { useNavigate } from "react-router-dom";
import { useState, useContext } from "react";
import { CurrentUserContext } from "App";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

export default function FriendsDailyUpdateComponent({
  avatar,
  postTitle,
  postBody,
  postMedia,
  numberOfLikes,
  dailyUpdateId,
  username,
  createdBy,
  onDelete,
}) {
  const navigate = useNavigate();
  const { currentUser } = useContext(CurrentUserContext);
  const [likes, setLikes] = useState(numberOfLikes);
  const [liked, setLiked] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [saved, setSaved] = useState(false);
  const isOwnPost = Number(currentUser?.user_id) === Number(createdBy);

  function addLike() {
    const endpoint = liked ? "/removeDailyUpdateLike" : "/addDailyUpdateLike";
    fetch(`${API_URL}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ dailyUpdateID: dailyUpdateId }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          setLikes((prev) => (liked ? prev - 1 : prev + 1));
          setLiked(!liked);
        }
      })
      .catch((err) => console.error(err));
  }

  function handleDelete() {
    if (!window.confirm("Delete this memory?")) return;
    fetch(`${API_URL}/deleteDailyUpdate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ daily_update_id: dailyUpdateId }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          setDeleted(true);
          if (onDelete) onDelete(dailyUpdateId);
        }
      })
      .catch((err) => console.error(err));
  }

  function handleShare() {
    const url = `${window.location.origin}/memory/${dailyUpdateId}`;
    if (navigator.share) {
      navigator.share({ title: postTitle, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => {
        alert("Link copied!");
      }).catch(() => {});
    }
  }

  if (deleted) return null;

  return (
    <div style={styles.card}>
      {/* Top row: bookmark + delete/share icons */}
      <div style={styles.topRow}>
        <div
          onClick={() => setSaved(!saved)}
          style={styles.iconBtn}
          title={saved ? "Unsave" : "Save"}
        >
          <ion-icon
            name={saved ? "bookmark" : "bookmark-outline"}
            style={{ fontSize: "1.1rem", color: "var(--main-purple)" }}
          ></ion-icon>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {isOwnPost && (
            <div
              onClick={handleDelete}
              style={styles.iconBtn}
              title="Delete memory"
            >
              <ion-icon
                name="trash-outline"
                style={{ fontSize: "1.1rem", color: "#ff6666" }}
              ></ion-icon>
            </div>
          )}
          <div
            onClick={handleShare}
            style={styles.iconBtn}
            title="Share"
          >
            <ion-icon
              name="share-outline"
              style={{ fontSize: "1.1rem", color: "var(--main-purple)" }}
            ></ion-icon>
          </div>
        </div>
      </div>

      {/* Center content: avatar or image */}
      <div style={styles.centerArea}>
        {postMedia ? (
          <img
            src={postMedia}
            alt="Post attachment"
            style={styles.postImage}
          />
        ) : (
          <div style={styles.avatarWrapper}>
            <img
              src={avatar}
              alt=""
              style={styles.avatar}
            />
          </div>
        )}
      </div>

      {/* Post text */}
      <div style={styles.textArea}>
        {postTitle && (
          <div style={styles.postTitle}>{postTitle}</div>
        )}
        {postBody && (
          <p style={styles.postBody}>{postBody}</p>
        )}
        {username && (
          <div style={styles.username}>@{username}</div>
        )}
      </div>

      {/* Bottom action bar */}
      <div style={styles.bottomBar}>
        <div
          onClick={addLike}
          style={{
            ...styles.bottomAction,
            color: liked ? "var(--main-purple)" : "rgba(185,151,255,0.6)",
            cursor: "pointer",
          }}
        >
          <ion-icon
            name={liked ? "heart" : "heart-outline"}
            style={{
              fontSize: "1.15rem",
              color: liked ? "var(--main-purple)" : "rgba(185,151,255,0.6)",
            }}
          ></ion-icon>
          <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>{likes}</span>
        </div>
        <div
          onClick={() => navigate("/connect")}
          style={{
            ...styles.bottomAction,
            color: "rgba(185,151,255,0.6)",
            cursor: "pointer",
          }}
        >
          <ion-icon
            name="chatbubble-outline"
            style={{ fontSize: "1.15rem", color: "rgba(185,151,255,0.6)" }}
          ></ion-icon>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: "rgba(30, 25, 45, 0.95)",
    borderRadius: "16px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    border: "1px solid rgba(185,151,255,0.1)",
    minWidth: 0,
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.5rem 0.65rem 0",
  },
  iconBtn: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    backgroundColor: "rgba(185,151,255,0.08)",
  },
  centerArea: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0.6rem 0.65rem 0.4rem",
  },
  avatarWrapper: {
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    overflow: "hidden",
    border: "2px solid rgba(185,151,255,0.25)",
    flexShrink: 0,
  },
  avatar: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  postImage: {
    width: "100%",
    maxHeight: "160px",
    objectFit: "cover",
    borderRadius: "10px",
  },
  textArea: {
    padding: "0 0.65rem 0.5rem",
    textAlign: "center",
    flex: 1,
    minHeight: 0,
  },
  postTitle: {
    fontSize: "0.82rem",
    fontWeight: 700,
    color: "#e0d6f2",
    marginBottom: "0.1rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  postBody: {
    fontSize: "0.75rem",
    color: "rgba(214,214,214,0.7)",
    lineHeight: 1.35,
    margin: 0,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  username: {
    fontSize: "0.68rem",
    color: "rgba(185,151,255,0.55)",
    marginTop: "0.2rem",
  },
  bottomBar: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    backgroundColor: "rgba(50, 40, 75, 0.6)",
    padding: "0.45rem 0.65rem",
    marginTop: "auto",
  },
  bottomAction: {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
};
